import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
    MdArrowBack,
    MdCheckCircle,
    MdCancel,
    MdPerson,
    MdStore,
    MdReceipt,
    MdInventory,
    MdImage,
    MdWarning,
} from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { fetchRetailerPendingBill, approveRetailerBill, rejectRetailerBill } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyProvider';
import { DEFAULT_CURRENCY } from '../../../utils/currencyFormatter';

const RetailerBillApprovalDetail = () => {
    const { t } = useTranslation();
    const { formatAmountIn } = useCurrency();
    const { id } = useParams();
    const navigate = useNavigate();
    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [reviewNotes, setReviewNotes] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const rejectReasonOptions = [
        { value: '', label: t('BillApprovalDetail.rejectModal.selectReason') },
        { value: 'invalid_receipt', label: t('BillApprovalDetail.rejectModal.reasons.invalidReceipt') },
        { value: 'wrong_store', label: t('BillApprovalDetail.rejectModal.reasons.wrongStore') },
        { value: 'duplicate', label: t('BillApprovalDetail.rejectModal.reasons.duplicate') },
        { value: 'unreadable', label: t('BillApprovalDetail.rejectModal.reasons.unreadable') },
        { value: 'items_mismatch', label: t('BillApprovalDetail.rejectModal.reasons.itemsMismatch') },
        { value: 'other', label: t('BillApprovalDetail.rejectModal.reasons.other') },
    ];

    const loadBill = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchRetailerPendingBill(id);
            setBill(data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadBill();
    }, [loadBill]);

    const handleApprove = async () => {
        if (processing) return;
        try {
            setProcessing(true);
            await approveRetailerBill(id, { notes: reviewNotes });
            navigate('/bill-approvals', { state: { message: t('BillApprovalDetail.approveSuccess') } });
        } catch (err) {
            setError(err);
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (processing || !rejectionReason.trim()) return;
        try {
            setProcessing(true);
            await rejectRetailerBill(id, {
                reason: rejectionReason,
                notes: reviewNotes
            });
            setShowRejectModal(false);
            navigate('/bill-approvals', { state: { message: t('BillApprovalDetail.rejectSuccess') } });
        } catch (err) {
            setError(err);
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending_retailer: { label: t('BillApprovals.status.pending'), color: '#F59E0B', bg: '#FEF3C7' },
            retailer_approved: { label: t('BillApprovals.status.approved'), color: '#22C55E', bg: '#DCFCE7' },
            retailer_rejected: { label: t('BillApprovals.status.rejected'), color: '#EF4444', bg: '#FEE2E2' },
        };
        const config = statusConfig[status] || { label: status, color: '#6B7280', bg: '#F3F4F6' };
        return <StatusBadge $color={config.color} $bg={config.bg}>{config.label}</StatusBadge>;
    };

    const getBillCurrencyCode = () => {
        const code = bill?.currency_code || bill?.currency || DEFAULT_CURRENCY;
        if (typeof code !== 'string') return DEFAULT_CURRENCY;
        const normalized = code.trim().toUpperCase();
        return normalized.length >= 3 && normalized.length <= 4 ? normalized : DEFAULT_CURRENCY;
    };

    if (loading) return <LoadingMessage>{t('BillApprovalDetail.loading')}</LoadingMessage>;
    if (error) return <ErrorMessage>{t('BillApprovalDetail.error')}</ErrorMessage>;
    if (!bill) return <ErrorMessage>{t('BillApprovalDetail.notFound')}</ErrorMessage>;

    const isPending = bill.status === 'pending_retailer';

    return (
        <PageContainer>
            <PageHeader>
                <HeaderRow>
                    <BackButton onClick={() => navigate('/bill-approvals')}>
                        <MdArrowBack size={20} />
                        {t('BillApprovalDetail.back')}
                    </BackButton>
                    <HeaderInfo>
                        <Title>{t('BillApprovalDetail.title')}</Title>
                        {getStatusBadge(bill.status)}
                    </HeaderInfo>
                </HeaderRow>
            </PageHeader>

            <PageContent>
                <ContentGrid>
                    {/* Left Column - Bill Details */}
                    <MainColumn>
                        {/* Customer Info */}
                        <Section>
                            <SectionHeader>
                                <MdPerson size={20} />
                                <SectionTitle>{t('BillApprovalDetail.sections.customer')}</SectionTitle>
                            </SectionHeader>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.name')}</InfoLabel>
                                    <InfoValue>{bill.user?.name || '-'}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.email')}</InfoLabel>
                                    <InfoValue>{bill.user?.email || '-'}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.phone')}</InfoLabel>
                                    <InfoValue>{bill.user?.phone || '-'}</InfoValue>
                                </InfoItem>
                            </InfoGrid>
                        </Section>

                        {/* Venue Info */}
                        <Section>
                            <SectionHeader>
                                <MdStore size={20} />
                                <SectionTitle>{t('BillApprovalDetail.sections.venue')}</SectionTitle>
                                {bill.venue_is_virtual && (
                                    <VirtualBadge>{t('BillApprovalDetail.virtualVenue')}</VirtualBadge>
                                )}
                            </SectionHeader>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.venueName')}</InfoLabel>
                                    <InfoValue>{bill.venue?.name || '-'}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.address')}</InfoLabel>
                                    <InfoValue>{bill.venue?.address || '-'}</InfoValue>
                                </InfoItem>
                            </InfoGrid>
                        </Section>

                        {/* Bill Details */}
                        <Section>
                            <SectionHeader>
                                <MdReceipt size={20} />
                                <SectionTitle>{t('BillApprovalDetail.sections.billDetails')}</SectionTitle>
                            </SectionHeader>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.billNumber')}</InfoLabel>
                                    <InfoValue>{bill.bill_number || bill.external_bill_id || '-'}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.billDate')}</InfoLabel>
                                    <InfoValue>
                                        {bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : '-'}
                                    </InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.total')}</InfoLabel>
                                    <InfoValue $highlight>
                                        {formatAmountIn(bill.total_amount || 0, getBillCurrencyCode(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>{t('BillApprovalDetail.fields.paymentMethod')}</InfoLabel>
                                    <InfoValue>{bill.payment_method || '-'}</InfoValue>
                                </InfoItem>
                            </InfoGrid>
                        </Section>

                        {/* Items */}
                        <Section>
                            <SectionHeader>
                                <MdInventory size={20} />
                                <SectionTitle>
                                    {t('BillApprovalDetail.sections.items')} ({bill.items?.length || 0})
                                </SectionTitle>
                            </SectionHeader>
                            {bill.items && bill.items.length > 0 ? (
                                <ItemsTable>
                                    <thead>
                                        <tr>
                                            <th>{t('BillApprovalDetail.items.product')}</th>
                                            <th>{t('BillApprovalDetail.items.quantity')}</th>
                                            <th>{t('BillApprovalDetail.items.price')}</th>
                                            <th>{t('BillApprovalDetail.items.total')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bill.items.map((item, idx) => (
                                            <tr key={item.id || idx}>
                                                <td>
                                                    <ProductInfo>
                                                        <ProductName>
                                                            {item.product_name || item.manufacturer_item?.product_name || '-'}
                                                        </ProductName>
                                                        {item.is_virtual && (
                                                            <VirtualTag>{t('BillApprovalDetail.virtualProduct')}</VirtualTag>
                                                        )}
                                                    </ProductInfo>
                                                </td>
                                                <td>{item.quantity || 1}</td>
                                                <td>{formatAmountIn(item.unit_price || 0, getBillCurrencyCode(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                <td>{formatAmountIn(item.total_price || item.unit_price || 0, getBillCurrencyCode(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </ItemsTable>
                            ) : (
                                <EmptyItems>{t('BillApprovalDetail.noItems')}</EmptyItems>
                            )}
                        </Section>
                    </MainColumn>

                    {/* Right Column - Images & Actions */}
                    <SideColumn>
                        {/* Bill Images */}
                        {bill.images && bill.images.length > 0 && (
                            <Section>
                                <SectionHeader>
                                    <MdImage size={20} />
                                    <SectionTitle>{t('BillApprovalDetail.sections.images')}</SectionTitle>
                                </SectionHeader>
                                <ImagesGrid>
                                    {bill.images.map((img, idx) => (
                                        <ImageThumb
                                            key={img.id || idx}
                                            src={img.url || img.image_url}
                                            alt={`Bill image ${idx + 1}`}
                                            onClick={() => window.open(img.url || img.image_url, '_blank')}
                                        />
                                    ))}
                                </ImagesGrid>
                            </Section>
                        )}

                        {/* Review Notes */}
                        {isPending && (
                            <Section>
                                <SectionHeader>
                                    <SectionTitle>{t('BillApprovalDetail.sections.reviewNotes')}</SectionTitle>
                                </SectionHeader>
                                <NotesTextarea
                                    placeholder={t('BillApprovalDetail.notesPlaceholder')}
                                    value={reviewNotes}
                                    onChange={(e) => setReviewNotes(e.target.value)}
                                />
                            </Section>
                        )}

                        {/* Actions */}
                        {isPending && (
                            <ActionsSection>
                                <WarningBox>
                                    <MdWarning size={20} />
                                    <span>{t('BillApprovalDetail.approvalWarning')}</span>
                                </WarningBox>
                                <ActionButtons>
                                    <ApproveButton onClick={handleApprove} disabled={processing}>
                                        <MdCheckCircle size={20} />
                                        {processing ? t('BillApprovalDetail.processing') : t('BillApprovalDetail.approve')}
                                    </ApproveButton>
                                    <RejectButton onClick={() => setShowRejectModal(true)} disabled={processing}>
                                        <MdCancel size={20} />
                                        {t('BillApprovalDetail.reject')}
                                    </RejectButton>
                                </ActionButtons>
                            </ActionsSection>
                        )}

                        {/* Already Reviewed */}
                        {!isPending && (
                            <ReviewedSection>
                                <ReviewedHeader $approved={bill.status === 'retailer_approved'}>
                                    {bill.status === 'retailer_approved' ? (
                                        <MdCheckCircle size={24} />
                                    ) : (
                                        <MdCancel size={24} />
                                    )}
                                    <span>
                                        {bill.status === 'retailer_approved'
                                            ? t('BillApprovalDetail.alreadyApproved')
                                            : t('BillApprovalDetail.alreadyRejected')
                                        }
                                    </span>
                                </ReviewedHeader>
                                {bill.retailer_reviewed_at && (
                                    <ReviewedInfo>
                                        <span>{t('BillApprovalDetail.reviewedAt')}</span>
                                        <strong>{new Date(bill.retailer_reviewed_at).toLocaleString()}</strong>
                                    </ReviewedInfo>
                                )}
                                {bill.retailer_review_notes && (
                                    <ReviewedInfo>
                                        <span>{t('BillApprovalDetail.reviewNotes')}</span>
                                        <p>{bill.retailer_review_notes}</p>
                                    </ReviewedInfo>
                                )}
                                {bill.retailer_rejection_reason && (
                                    <ReviewedInfo>
                                        <span>{t('BillApprovalDetail.rejectionReason')}</span>
                                        <p>{bill.retailer_rejection_reason}</p>
                                    </ReviewedInfo>
                                )}
                            </ReviewedSection>
                        )}
                    </SideColumn>
                </ContentGrid>
            </PageContent>

            {/* Rejection Modal */}
            {showRejectModal && (
                <ModalOverlay onClick={() => setShowRejectModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <MdCancel size={24} color="#EF4444" />
                            <h3>{t('BillApprovalDetail.rejectModal.title')}</h3>
                        </ModalHeader>
                        <ModalBody>
                            <p>{t('BillApprovalDetail.rejectModal.description')}</p>
                            <ReasonSelect
                                value={rejectionReason}
                                onChange={(event, nextValue) => setRejectionReason(nextValue ?? event?.target?.value ?? '')}
                                options={rejectReasonOptions}
                                inputId="bill-reject-reason"
                            />
                        </ModalBody>
                        <ModalFooter>
                            <CancelButton onClick={() => setShowRejectModal(false)}>
                                {t('BillApprovalDetail.rejectModal.cancel')}
                            </CancelButton>
                            <ConfirmRejectButton
                                onClick={handleReject}
                                disabled={!rejectionReason.trim() || processing}
                            >
                                {processing ? t('BillApprovalDetail.processing') : t('BillApprovalDetail.rejectModal.confirm')}
                            </ConfirmRejectButton>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

export default RetailerBillApprovalDetail;

// Styled Components
const HeaderRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: #6B7280;
    font-size: 0.875rem;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: rgba(130, 22, 146, 1);
    }
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const Title = styled.h1`
    margin: 0;
    color: rgba(130, 22, 146, 1);
    font-size: 1.5rem;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.875rem;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 1.5rem;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const MainColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const SideColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const Section = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-bottom: 1rem;
    color: rgba(130, 22, 146, 1);
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
`;

const VirtualBadge = styled.span`
    padding: 0.125rem 0.5rem;
    background: #FEF3C7;
    color: #92400E;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-weight: 600;
    margin-left: auto;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const InfoLabel = styled.span`
    font-size: 0.75rem;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
    font-size: 0.9375rem;
    color: #111827;
    font-weight: ${({ $highlight }) => $highlight ? '600' : '400'};
    color: ${({ $highlight }) => $highlight ? 'rgba(130, 22, 146, 1)' : '#111827'};
`;

const ItemsTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        padding: 0.75rem;
        text-align: left;
        border-bottom: 1px solid #E5E7EB;
    }

    th {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6B7280;
        text-transform: uppercase;
        background: #F9FAFB;
    }

    td {
        font-size: 0.875rem;
        color: #374151;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }
`;

const ProductInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const ProductName = styled.span`
    font-weight: 500;
`;

const VirtualTag = styled.span`
    display: inline-block;
    padding: 0.125rem 0.375rem;
    background: #FEF3C7;
    color: #92400E;
    border-radius: 4px;
    font-size: 0.625rem;
    font-weight: 600;
    width: fit-content;
`;

const EmptyItems = styled.div`
    text-align: center;
    padding: 1.5rem;
    color: #6B7280;
`;

const ImagesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
`;

const ImageThumb = styled.img`
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
        transform: scale(1.02);
    }
`;

const NotesTextarea = styled.textarea`
    width: 100%;
    min-height: 100px;
    padding: 0.75rem;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: rgba(130, 22, 146, 0.5);
        box-shadow: 0 0 0 3px rgba(130, 22, 146, 0.1);
    }
`;

const ActionsSection = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const WarningBox = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem;
    background: #FEF3C7;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-size: 0.8125rem;
    color: #92400E;

    svg {
        flex-shrink: 0;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const ApproveButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.875rem;
    background: #22C55E;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
        background: #16A34A;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const RejectButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.875rem;
    background: white;
    color: #EF4444;
    border: 2px solid #EF4444;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: #FEE2E2;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ReviewedSection = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ReviewedHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem;
    background: ${({ $approved }) => $approved ? '#DCFCE7' : '#FEE2E2'};
    color: ${({ $approved }) => $approved ? '#166534' : '#991B1B'};
    border-radius: 8px;
    margin-bottom: 1rem;
    font-weight: 600;
`;

const ReviewedInfo = styled.div`
    margin-bottom: 0.75rem;

    span {
        display: block;
        font-size: 0.75rem;
        color: #6B7280;
        margin-bottom: 0.25rem;
    }

    strong, p {
        color: #374151;
        margin: 0;
    }
`;

// Modal Styles
const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 480px;
    margin: 1.25rem;
    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1.25rem;
    border-bottom: 1px solid #E5E7EB;

    h3 {
        margin: 0;
        font-size: 1.125rem;
        color: #111827;
    }
`;

const ModalBody = styled.div`
    padding: 1.25rem;

    p {
        margin: 0 0 1rem 0;
        color: #6B7280;
    }
`;

const ReasonSelect = styled(SimpleSelect)`
    width: 100%;
    font-size: 0.875rem;
    cursor: pointer;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.25rem;
    border-top: 1px solid #E5E7EB;
`;

const CancelButton = styled.button`
    padding: 0.625rem 1.25rem;
    background: white;
    color: #374151;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;

    &:hover {
        background: #F3F4F6;
    }
`;

const ConfirmRejectButton = styled.button`
    padding: 0.625rem 1.25rem;
    background: #EF4444;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #DC2626;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
